import styled from '@emotion/styled';
import {
  duration,
  easing,
  gradient,
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  typography,
} from '../../../style-utils/theme';
import { touch, up } from '../../../style-utils/media-queries';

export type GridDensity = 'picker' | 'calendar';

/** Where a cell sits in the selection band: the band runs edge to edge
 *  between the two ends and starts / stops at the middle of an end cell. */
export type BandPart = 'none' | 'start' | 'middle' | 'end' | 'single';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  min-width: 0;
`;

export const Heading = styled.h3`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  text-align: center;
`;

export const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  width: 100%;
  min-width: 0;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  width: 100%;
  min-width: 0;
`;

export const ColumnHeader = styled.div`
  min-width: 0;
  overflow: hidden;
  padding: ${spacing.half} 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.slate};
  text-align: center;
  white-space: nowrap;
`;

/* The cell carries the selection BAND (a pseudo-element behind the button) so
   the tint runs unbroken across a week; the button carries the ends. */
export const Cell = styled.div<{ $band: BandPart }>`
  position: relative;
  display: flex;
  min-width: 0;
  touch-action: manipulation;

  &::before {
    content: '';
    display: ${({ $band }) =>
      $band === 'none' || $band === 'single' ? 'none' : 'block'};
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${({ $band }) => ($band === 'start' ? '50%' : '0')};
    right: ${({ $band }) => ($band === 'end' ? '50%' : '0')};
    background: ${palette.tealTint};
  }
`;

/* A day: ONE native button per cell. 40px at a mouse, 44px under a thumb, a
   seventh of the grid wide (the Row columns), never wider than its cell.
   touch-action manipulation: a vertical swipe that starts on a day still
   scrolls the page, and a double tap never zooms.
   NO BACKTICKS IN THIS COMMENT — it sits inside a styled template literal. */
export const DayButton = styled.button<{ $density: GridDensity }>`
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: ${({ $density }) => ($density === 'calendar' ? 'stretch' : 'center')};
  justify-content: ${({ $density }) =>
    $density === 'calendar' ? 'flex-start' : 'center'};
  gap: ${spacing.half};
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: ${({ $density }) => ($density === 'calendar' ? space(8) : space(5))};
  margin: 0;
  padding: ${spacing.half};
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.snug};
  font-variant-numeric: tabular-nums;
  text-align: center;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard};

  &:hover:not([aria-disabled='true']):not([data-edge='true']) {
    background: ${palette.irisTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &[data-outside='true'] {
    color: ${palette.slateLight};
  }

  &[data-edge='true'] {
    background: ${gradient.primary};
    color: ${palette.white};
  }

  &[data-today='true'] [data-day-number] {
    box-shadow: ${shadow.focus};
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    color: ${palette.slateDisabled};
  }

  ${up('md')} {
    min-height: ${({ $density }) => ($density === 'calendar' ? space(14) : space(5))};
  }

  ${touch} {
    ${({ $density }) =>
      $density === 'picker' ? `min-height: ${layout.touchTarget};` : ''}

    &:hover:not([data-edge='true']) {
      background: transparent;
    }
  }
`;

/* The number inside a day. The grid draws the today ring around it, so a
   renderer that wants the ring renders its number in this. */
export const DayNumberSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex: none;
  width: ${space(4)};
  height: ${space(4)};
  border-radius: ${radius.circle};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

export const EmptyCell = styled.div`
  min-width: 0;
`;
